import CloseIcon from '@mui/icons-material/Close'
import { Drawer, IconButton, Tooltip } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { getOrderDetails } from 'api/orderManagement'
import {
  DeliveryIncluded,
  ModalType,
  OrderDetails,
  PaymentProvider,
  PaymentProviderIncluded,
  ReportingOrder
} from 'api/orderManagement/types'
import { AlertContainer, LoadingSkeleton, Toast } from 'components'
import PrimaryButton from 'components/modern/PrimaryButton'
import ScrollIndicator from 'components/ScrollIndicator/ScrollIndicator'
import { useAuth0 } from 'libs/auth0-react'
import React, { useEffect, useRef, useState } from 'react'
import { PlatformFriendlyName, PlatformName, ReportingOrdersPlatformName } from 'types'
import { formatCurrency, formatSubmissionDate, formatSubmissionTime } from 'utils'
import { formatPhoneNumber } from 'utils/phone'
import { getOrderRawPayloadURL } from 'utils/urls'

import * as Styled from './OrderDetailsDrawerStyles'
import { OrderDisplayStatus, statusConfig } from './OrderManagement'

interface OrderDetailsDrawerProps {
  isOpen: boolean
  onClose: () => void
  internalOrderId: number | null
  onModalOpen: (order: ReportingOrder, type: ModalType, mapOrderId?: string) => void
}

export function translateOrderStatus(status: string | undefined): OrderDisplayStatus {
  switch (status) {
    case 'finalized':
    case 'posted':
    case 'payment_captured':
    case 'cancellation_failed':
      return OrderDisplayStatus.COMPLETED

    case 'cancelled':
      return OrderDisplayStatus.CANCELLED

    case 'errored':
    case 'error':
    case 'failed':
    case 'erred':
    case 'payment_erred':
      return OrderDisplayStatus.ERRORED

    default:
      return OrderDisplayStatus.ALL
  }
}

// Helper function to check if phone number is a dummy number (starts with 555)
function isDummyPhoneNumber(phoneNumber: string | undefined): boolean {
  if (!phoneNumber) return false
  // Clean the phone number and check if it starts with 555
  const cleaned = phoneNumber.replace(/\D/g, '')
  return cleaned.startsWith('555')
}

// Helper function to check if platform is a third-party platform (DoorDash or UberEats)
function isThirdPartyPlatform(platformName: string | undefined): boolean {
  return (
    platformName === ReportingOrdersPlatformName.DOORDASH ||
    platformName === ReportingOrdersPlatformName.UBEREATS
  )
}

// Helper function to check if an order is completed
function isOrderCompleted(orderStatus: string | undefined, aasmState: string | null | undefined): boolean {
  // Check if order_status is 'completed'
  if (translateOrderStatus(orderStatus) === OrderDisplayStatus.COMPLETED) return true
  
  // Check if aasm_state indicates a completed order
  if (aasmState && ['finalized', 'posted', 'payment_captured'].includes(aasmState)) return true
  
  return false
}

const OrderDetailsDrawer: React.FC<OrderDetailsDrawerProps> = ({
  isOpen,
  onClose,
  internalOrderId,
  onModalOpen
}) => {
  const { user } = useAuth0()

  const contentRef = useRef<HTMLDivElement>(null)
  const [order, setOrder] = useState<OrderDetails | null>(null)
  const [included, setIncluded] = useState<any[] | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isScrollable, setIsScrollable] = useState(false)
  const isDesktop = useMediaQuery('(min-width: 900px)')

  const isAdminUser = user?.email?.includes('@chowlyinc.com')

  const IFRAME_WIDTH = 792
  const ORDER_DETAILS_WIDTH = 420

  // Reset state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setOrder(null)
      setIncluded(null)
      setIsLoading(false)
    }
  }, [isOpen])

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (isOpen && internalOrderId) {
        setIsLoading(true)
        // Reset state before fetching new data
        setOrder(null)
        setIncluded(null)
        try {
          const response = await getOrderDetails(String(internalOrderId))
          setOrder(response.data)
          setIncluded(response.included || [])
        } catch (error) {
          console.error('Error fetching order details:', error)
        } finally {
          setIsLoading(false)
        }
      }
    }

    fetchOrderDetails()
  }, [isOpen, internalOrderId])

  const delivery = included?.find((inc) => inc.type === 'delivery') as DeliveryIncluded | undefined
  const payment = included?.find((inc) => inc.type === 'payment_provider') as
    | PaymentProviderIncluded
    | undefined
  const paymentProvider = payment?.attributes?.kind?.toLocaleLowerCase() || ''

  const iframeUrl = delivery?.attributes?.iframe_url

  useEffect(() => {
    const checkScrollable = () => {
      if (contentRef.current) {
        setIsScrollable(contentRef.current.scrollHeight > contentRef.current.clientHeight)
      }
    }
    checkScrollable()
    window.addEventListener('resize', checkScrollable)
    return () => window.removeEventListener('resize', checkScrollable)
  }, [order, isLoading, iframeUrl, isDesktop])

  const {
    order_platform_order_id,
    platform_name,
    submission_time,
    pos_ticket_id,
    customer_first_name,
    customer_last_name,
    customer_phone,
    customer_email,
    subtotal,
    tax,
    tip,
    delivery_fee,
    order_type,
    order_status,
    aasm_state,
    delivery_street,
    delivery_street2,
    delivery_city,
    delivery_state,
    delivery_zip,
    special_instructions,
    internal_order_id,
    completed_at,
    future,
    future_time,
    placed_at,
    payment_type,
    total,
    discount_amount,
    order_items,
    pickup_info,
    location_name,
    partner_name,
    service_fee_amount,
    total_without_service_fee,
    external_order_manager_url: externalOrderManagerUrl,
  } = order?.attributes || {}

  const isDoorDashOrder = platform_name === PlatformFriendlyName.DOORDASH

  const renderSkeleton = () => (
    <div>
      <Styled.SectionHeader>
        <LoadingSkeleton width={200} />
      </Styled.SectionHeader>
      {[...Array(20)].map((_, index) => (
        <React.Fragment key={`skeleton-${index}`}>
          <Styled.Row>
            <Styled.Label>
              <LoadingSkeleton width={80} />
            </Styled.Label>
            <Styled.Value>
              <LoadingSkeleton width={250} />
            </Styled.Value>
          </Styled.Row>
          {index % 5 === 0 && (
            <React.Fragment key={`divider-${index}`}>
              <Styled.Divider />
              <Styled.SectionHeader>
                <LoadingSkeleton width={200} />
              </Styled.SectionHeader>
            </React.Fragment>
          )}
        </React.Fragment>
      ))}
    </div>
  )

  return (
    <Drawer
      anchor='right'
      open={isOpen}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          maxWidth: '100vw',
          width:
            (iframeUrl || externalOrderManagerUrl) && isDesktop && isAdminUser
              ? `min(calc(${IFRAME_WIDTH}px + ${ORDER_DETAILS_WIDTH}px), 100vw)`
              : null,
          padding: 0,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden'
        }
      }}
    >
      {internalOrderId ? (
        <Styled.DrawerContentContainer hasIframe={!!(iframeUrl || externalOrderManagerUrl)} isDesktop={isDesktop}>
          {iframeUrl && isDesktop && isAdminUser && (
            <Styled.IframeContainer>
              <div>
                <Styled.HeaderRow>
                  <div>
                    <Styled.SectionHeader data-testid='delivery-details-header'>
                      Delivery Details
                    </Styled.SectionHeader>
                  </div>
                </Styled.HeaderRow>
              </div>
              <Styled.IframeWrapper>
                <iframe
                  src={iframeUrl}
                  width={IFRAME_WIDTH}
                  height='100%'
                  style={{ borderRadius: 8, width: '100%', height: '95%' }}
                  title='Nash Delivery Details'
                  loading='lazy'
                  sandbox='allow-scripts allow-popups allow-same-origin'
                />
              </Styled.IframeWrapper>
            </Styled.IframeContainer>
          )}
          {externalOrderManagerUrl && isDesktop && isAdminUser && (
            <Styled.IframeContainer>
              <div>
                <Styled.HeaderRow>
                  <div>
                    <Styled.SectionHeader data-testid='delivery-details-header'>
                      Doordash Order Help
                    </Styled.SectionHeader>
                  </div>
                </Styled.HeaderRow>
              </div>
              <Styled.IframeWrapper>
                <iframe
                  src={externalOrderManagerUrl}
                  width={IFRAME_WIDTH}
                  height='100%'
                  style={{ borderRadius: 8, width: '100%', height: '95%' }}
                  title='Doordash Order Help'
                  loading='lazy'
                  sandbox='allow-scripts allow-popups allow-same-origin'
                />
              </Styled.IframeWrapper>
            </Styled.IframeContainer>
          )}
          <Styled.OrderDetailsContainer>
            {/* Header (fixed) */}
            <div>
              <Styled.HeaderRow>
                <div>
                  <Styled.StyledChip
                    label={translateOrderStatus(order_status)}
                    icon={
                      statusConfig[translateOrderStatus(order_status) as OrderDisplayStatus].icon
                    }
                    status={translateOrderStatus(order_status) as OrderDisplayStatus}
                    size='medium'
                    sx={{ flexShrink: 0 }}
                  />
                  {isDoorDashOrder && (
                    <Styled.ReadyForPickupSection>
                      <PrimaryButton
                        onClick={() => {
                          if (order) {
                            onModalOpen({} as ReportingOrder, ModalType.ready_for_pickup, order.id)
                          }
                        }}
                        sx={{
                          minWidth: '120px',
                          px: 2,
                          height: 32,
                          flexShrink: 0,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Ready for Pickup
                      </PrimaryButton>
                      {completed_at && (
                        <Styled.MarkedReadyForPickup>
                          Marked at {formatSubmissionTime(completed_at)}
                        </Styled.MarkedReadyForPickup>
                      )}
                    </Styled.ReadyForPickupSection>
                  )}
                </div>
                <IconButton onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Styled.HeaderRow>
              <Styled.Divider />
            </div>

            {/* Body (scrollable) */}
            <Styled.ScrollableContent ref={contentRef}>
              {isLoading ? (
                renderSkeleton()
              ) : (
                <Styled.OrderDetailsContent>
                  <Styled.SectionHeader>{location_name || partner_name}</Styled.SectionHeader>
                  <Styled.Row>
                    <Styled.Label>Platform:</Styled.Label>
                    {isAdminUser ? (
                      <Tooltip title='Open JSON Payload' arrow>
                        <Styled.CustomLink
                          data-testid='platform-payload-link'
                          onClick={() => {
                            if (!internal_order_id) {
                              Toast.error(
                                { message: 'Unable to open order payload. Please try again.' },
                                { autoClose: 5000 }
                              )
                              return
                            }
                            window.open(
                              getOrderRawPayloadURL(String(internal_order_id)),
                              '_blank',
                              'noopener,noreferrer'
                            )
                          }}
                        >
                          {platform_name}
                        </Styled.CustomLink>
                      </Tooltip>
                    ) : (
                      <Styled.Value horizontal>{platform_name}</Styled.Value>
                    )}
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>Platform Ticket ID:</Styled.Label>
                    <Styled.Value>
                      <Styled.PlatformTicket>{order_platform_order_id}</Styled.PlatformTicket>
                    </Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>POS Ticket ID:</Styled.Label>
                    <Styled.WrappedValue>{pos_ticket_id}</Styled.WrappedValue>
                  </Styled.Row>
                  <Styled.Divider />
                  <Styled.SectionHeader>
                    {customer_first_name} {customer_last_name}
                  </Styled.SectionHeader>
                  <Styled.Row>
                    <Styled.Label>Phone:</Styled.Label>
                    <Styled.InfoItem>
                      {isThirdPartyPlatform(platform_name) && isDummyPhoneNumber(customer_phone) ? (
                        <span>{formatPhoneNumber(customer_phone || '')}</span>
                      ) : (
                        <a href={`tel:${customer_phone}`}>
                          {formatPhoneNumber(customer_phone || '')}
                        </a>
                      )}
                    </Styled.InfoItem>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>Email:</Styled.Label>
                    <Styled.InfoItem>
                      <a href={`mailto:${customer_email}`}>{customer_email}</a>
                    </Styled.InfoItem>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>Address:</Styled.Label>
                    <Styled.Value>
                      <Styled.PlatformTicket>{delivery_street}</Styled.PlatformTicket>
                    </Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>Address 2:</Styled.Label>
                    <Styled.Value>
                      <Styled.PlatformTicket>{delivery_street2}</Styled.PlatformTicket>
                    </Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>City:</Styled.Label>
                    <Styled.Value>
                      <Styled.PlatformTicket>{delivery_city}</Styled.PlatformTicket>
                    </Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>State:</Styled.Label>
                    <Styled.Value>
                      <Styled.PlatformTicket>{delivery_state}</Styled.PlatformTicket>
                    </Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>Zip:</Styled.Label>
                    <Styled.Value>
                      <Styled.PlatformTicket>{delivery_zip}</Styled.PlatformTicket>
                    </Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>Special Instructions:</Styled.Label>
                    <Styled.Value>
                      <Styled.PlatformTicket>{special_instructions}</Styled.PlatformTicket>
                    </Styled.Value>
                  </Styled.Row>
                  <Styled.Divider />
                  <Styled.SectionHeader>Order Details</Styled.SectionHeader>
                  <Styled.Row>
                    <Styled.Label>Created At:</Styled.Label>
                    <Styled.Value horizontal>
                      <Styled.SubmissionDate>
                        {formatSubmissionDate(submission_time || '')}
                      </Styled.SubmissionDate>
                      <Styled.SubmissionTime>
                        {formatSubmissionTime(submission_time || '')}
                      </Styled.SubmissionTime>
                    </Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>Completed At:</Styled.Label>
                    {completed_at && (
                      <Styled.Value horizontal>
                        <Styled.SubmissionDate>
                          {formatSubmissionDate(completed_at || '')}
                        </Styled.SubmissionDate>
                        <Styled.SubmissionTime>
                          {formatSubmissionTime(completed_at || '')}
                        </Styled.SubmissionTime>
                      </Styled.Value>
                    )}
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>Future?:</Styled.Label>
                    <Styled.Value>{future === 'true' ? 'Yes' : 'No'}</Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>Future Time:</Styled.Label>
                    {future_time && (
                      <Styled.Value horizontal>
                        <Styled.SubmissionDate>
                          {formatSubmissionDate(future_time || '')}
                        </Styled.SubmissionDate>
                        <Styled.SubmissionTime>
                          {formatSubmissionTime(future_time || '')}
                        </Styled.SubmissionTime>
                      </Styled.Value>
                    )}
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>Placed At:</Styled.Label>
                    {placed_at && (
                      <Styled.Value horizontal>
                        <Styled.SubmissionDate>
                          {formatSubmissionDate(placed_at)}
                        </Styled.SubmissionDate>
                        <Styled.SubmissionTime>
                          {formatSubmissionTime(placed_at)}
                        </Styled.SubmissionTime>
                      </Styled.Value>
                    )}
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>Pickup Info:</Styled.Label>
                    <Styled.Value>{pickup_info}</Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>Order Type:</Styled.Label>
                    <Styled.Value>{order_type}</Styled.Value>
                  </Styled.Row>
                  <Styled.Divider />
                  <Styled.SectionHeader>Item Details</Styled.SectionHeader>
                  {order_items?.map((item) => (
                    <React.Fragment key={item.id}>
                      <Styled.Row>
                        <Styled.Label>Name:</Styled.Label>
                        <Styled.Value>{item['menu-item-name']}</Styled.Value>
                      </Styled.Row>
                      <Styled.Row>
                        <Styled.Label>Quantity:</Styled.Label>
                        <Styled.Value>{item.quantity}</Styled.Value>
                      </Styled.Row>
                      <Styled.Row>
                        <Styled.Label>Modifiers:</Styled.Label>
                        <Styled.Value>{item['menu-choices']}</Styled.Value>
                      </Styled.Row>
                      <Styled.Row>
                        <Styled.Label>Special Instructions:</Styled.Label>
                        <Styled.Value>{item['special-instructions']}</Styled.Value>
                      </Styled.Row>
                      <Styled.Row>
                        <Styled.Label>Price:</Styled.Label>
                        <Styled.Value>{formatCurrency(item.price, '0.00')}</Styled.Value>
                      </Styled.Row>
                      <Styled.Divider />
                    </React.Fragment>
                  ))}
                  {order_items?.length === 0 && <Styled.Divider />}
                  <Styled.SectionHeader>Payment Details</Styled.SectionHeader>
                  <Styled.Row>
                    <Styled.Label>SubTotal:</Styled.Label>
                    <Styled.Value>{formatCurrency(subtotal, '0.00')}</Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>Delivery Charge:</Styled.Label>
                    <Styled.Value>{formatCurrency(delivery_fee, '0.00')}</Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>Tax:</Styled.Label>
                    <Styled.Value>{formatCurrency(tax, '0.00')}</Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>Discount Amount:</Styled.Label>
                    <Styled.Value>{formatCurrency(discount_amount, '0.00')}</Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>Tip:</Styled.Label>
                    <Styled.Value>{formatCurrency(tip, '0.00')}</Styled.Value>
                  </Styled.Row>
                  {isAdminUser ? (
                    <>
                      <Styled.Row>
                        <Styled.Label>Total:</Styled.Label>
                        <Styled.Value>{formatCurrency(total, '0.00')}</Styled.Value>
                      </Styled.Row>
                      <Styled.Row>
                        <Styled.Label>Total without Service Fee:</Styled.Label>
                        <Styled.Value>
                          {formatCurrency(total_without_service_fee, '0.00')}
                        </Styled.Value>
                      </Styled.Row>
                      <Styled.Row>
                        <Styled.Label>Service Fee:</Styled.Label>
                        <Styled.Value>{formatCurrency(service_fee_amount, '0.00')}</Styled.Value>
                      </Styled.Row>
                    </>
                  ) : (
                    <Styled.Row>
                      <Styled.Label>Total:</Styled.Label>
                      <Styled.Value>
                        {formatCurrency(total_without_service_fee, '0.00')}
                      </Styled.Value>
                    </Styled.Row>
                  )}
                  <Styled.Row>
                    <Styled.Label>Payment Type:</Styled.Label>
                    <Styled.Value>{payment_type}</Styled.Value>
                  </Styled.Row>
                  <Styled.Row>
                    <Styled.Label>Payment Provider:</Styled.Label>
                    <Styled.Value>{paymentProvider}</Styled.Value>
                  </Styled.Row>
                  {paymentProvider === PaymentProvider.square && (
                    <AlertContainer
                      messages='Square Pay payments can be refunded from the POS as needed'
                      type='secondary'
                    />
                  )}
                  {isScrollable && <ScrollIndicator contentRef={contentRef} isOpen={isOpen} />}
                  {iframeUrl && !isDesktop && isAdminUser && (
                    <Styled.MobileIframeContainer>
                      <Styled.Divider />
                      <Styled.SectionHeader data-testid='delivery-details-header'>
                        Delivery Details
                      </Styled.SectionHeader>
                      <iframe
                        src={iframeUrl}
                        style={{ borderRadius: 8, width: '100%', minHeight: 400 }}
                        title='Nash Delivery Details'
                        loading='lazy'
                        sandbox='allow-scripts allow-popups allow-same-origin'
                      />
                    </Styled.MobileIframeContainer>
                  )}
                  {externalOrderManagerUrl && !isDesktop && isAdminUser && (
                    <Styled.MobileIframeContainer>
                      <Styled.Divider />
                      <Styled.SectionHeader data-testid='doordash-details-header'>
                        Doordash Order Help
                      </Styled.SectionHeader>
                      <iframe
                        src={externalOrderManagerUrl}
                        style={{ borderRadius: 8, width: '100%', minHeight: 400 }}
                        title='Doordash Order Help'
                        loading='lazy'
                        sandbox='allow-scripts allow-popups allow-same-origin'
                      />
                    </Styled.MobileIframeContainer>
                  )}
                </Styled.OrderDetailsContent>
              )}
            </Styled.ScrollableContent>
            {/* Footer (fixed) */}
            {!isLoading && (
              <Styled.FixedButtonBar>
                {platform_name === PlatformName.COO && !isOrderCompleted(order_status, aasm_state || undefined) && (
                  <PrimaryButton
                    variant='outlined'
                    onClick={() => {
                      if (order) {
                        onModalOpen({} as ReportingOrder, ModalType.cancel, order.id)
                        onClose()
                      }
                    }}
                  >
                    Cancel Order
                  </PrimaryButton>
                )}
                {platform_name === PlatformName.COO && paymentProvider === PaymentProvider.stripe && (
                  <PrimaryButton
                    variant='outlined'
                    onClick={() => {
                      if (order) {
                        onModalOpen({} as ReportingOrder, ModalType.refund, order.id)
                        onClose()
                      }
                    }}
                  >
                    Refund Order
                  </PrimaryButton>
                )}
                <Tooltip title='Open JSON Payload' arrow>
                  <span>
                    <PrimaryButton
                      variant='outlined'
                      onClick={() => {
                        if (!internal_order_id) {
                          Toast.error(
                            { message: 'Unable to open order payload. Please try again.' },
                            { autoClose: 5000 }
                          )
                          return
                        }
                        window.open(
                          getOrderRawPayloadURL(String(internal_order_id)),
                          '_blank',
                          'noopener,noreferrer'
                        )
                      }}
                    >
                      Payload
                    </PrimaryButton>
                  </span>
                </Tooltip>
              </Styled.FixedButtonBar>
            )}
          </Styled.OrderDetailsContainer>
        </Styled.DrawerContentContainer>
      ) : null}
    </Drawer>
  )
}

export default OrderDetailsDrawer
